// src/components/layout/LegalSection.tsx
import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';

// --- Component Props ---
interface LegalSectionProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

/**
 * LegalSection: Renders a single, animated section of a legal document.
 * It's a forwardRef component so the parent layout can attach a ref for scroll-spying.
 */
export const LegalSection = forwardRef<HTMLDivElement, LegalSectionProps>(
  ({ id, title, children }, ref) => (
    <motion.section
      id={id}
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="py-6 scroll-mt-24 first:pt-0" // scroll-mt accounts for sticky header
    >
      {/* Do not render a title for the very first "intro" section of a document */}
      {id !== 'intro' && (
        <h2 className="text-3xl font-bold text-gray-900 border-b-2 border-slate-200 pb-4 mb-6">
            {title}
        </h2>
      )}
      {children}
    </motion.section>
  )
);

// Set a display name for the component for easier debugging in React DevTools.
LegalSection.displayName = "LegalSection";
